import { Link, NavLink } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { nav, wordmark } from "@/content/translations";

const LANG_NAMES = { pt: "Português", fr: "Français", en: "English" };

export default function Header() {
    const { lang, setLanguage, SUPPORTED } = useLanguage();
    const t = nav[lang];
    const [langOpen, setLangOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const langRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const chooseLang = (code) => {
        setLanguage(code);
        setLangOpen(false);
    };

    return (
        <header
            className="sticky top-0 z-40 bg-[hsl(var(--tm-ivory))]/85 backdrop-blur-md"
            style={{ boxShadow: "0 1px 0 rgba(49,46,42,0.04), 0 8px 24px -20px rgba(49,46,42,0.12)" }}
            data-testid="site-header"
        >
            <div className="max-w-[1360px] mx-auto px-8 sm:px-16 py-7 flex items-center justify-between gap-6">
                <Link to="/" data-testid="header-wordmark" className="group flex flex-col leading-none">
                    <span className="tm-wordmark text-[1.35rem] sm:text-[1.5rem] text-[hsl(var(--tm-anthracite))]">
                        Timemetria
                    </span>
                    <span className="mt-1.5 h-px w-10 bg-[hsl(var(--tm-gold))] group-hover:w-20 transition-all duration-700"></span>
                    <span className="tm-label mt-2">{wordmark[lang]}</span>
                </Link>

                <nav className="hidden md:flex items-center gap-10 tm-label" data-testid="header-nav">
                    <NavLink to="/" end className={({ isActive }) => `tm-nav-underline ${isActive ? "active text-[hsl(var(--tm-gold))]" : "tm-gold-link"}`} data-testid="nav-home">
                        {t.accueil}
                    </NavLink>
                    <NavLink to="/expertise" className={({ isActive }) => `tm-nav-underline ${isActive ? "active text-[hsl(var(--tm-gold))]" : "tm-gold-link"}`} data-testid="nav-expertise">
                        {t.expertise}
                    </NavLink>
                    <NavLink to="/a-propos" className={({ isActive }) => `tm-nav-underline ${isActive ? "active text-[hsl(var(--tm-gold))]" : "tm-gold-link"}`} data-testid="nav-apropos">
                        {t.apropos}
                    </NavLink>
                </nav>

                <div className="flex items-center gap-3">
                    {/* Language dropdown */}
                    <div className="relative" ref={langRef} data-testid="lang-switch">
                        <button
                            onClick={() => setLangOpen(!langOpen)}
                            className="tm-label flex items-center gap-2 px-3 py-2 border border-[hsl(var(--tm-hairline))] hover:border-[hsl(var(--tm-gold))] transition-colors duration-300"
                            aria-haspopup="true"
                            aria-expanded={langOpen}
                        >
                            <span className="text-[hsl(var(--tm-anthracite))]">{lang.toUpperCase()}</span>
                            <span
                                className="w-2 h-2 border-r border-b border-[hsl(var(--tm-muted))] transition-transform duration-300"
                                style={{ transform: langOpen ? "rotate(-135deg)" : "rotate(45deg)", marginTop: langOpen ? "3px" : "-2px" }}
                            ></span>
                        </button>
                        {langOpen && (
                            <div className="absolute right-0 mt-2 min-w-[140px] bg-[hsl(var(--tm-ivory))] border border-[hsl(var(--tm-hairline))] shadow-lg overflow-hidden">
                                {SUPPORTED.map((code) => (
                                    <button
                                        key={code}
                                        onClick={() => chooseLang(code)}
                                        className={`w-full text-left px-4 py-3 tm-label transition-colors duration-200 hover:bg-[hsl(var(--tm-ivory-deep))] ${lang === code ? "text-[hsl(var(--tm-gold))]" : "text-[hsl(var(--tm-anthracite-soft))]"}`}
                                    >
                                        {LANG_NAMES[code]}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mobile menu toggle */}
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        className="md:hidden flex flex-col gap-[5px] p-2"
                        aria-label="Menu"
                        data-testid="mobile-menu-toggle"
                    >
                        <span className={`w-5 h-px bg-[hsl(var(--tm-anthracite))] transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-[6px]" : ""}`}></span>
                        <span className={`w-5 h-px bg-[hsl(var(--tm-anthracite))] transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`}></span>
                        <span className={`w-5 h-px bg-[hsl(var(--tm-anthracite))] transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-[6px]" : ""}`}></span>
                    </button>
                </div>
            </div>

            {/* Mobile nav drawer */}
            {menuOpen && (
                <nav className="md:hidden border-t border-[hsl(var(--tm-hairline))] bg-[hsl(var(--tm-ivory))] px-8 py-6 flex flex-col gap-5 tm-label" data-testid="mobile-nav">
                    <NavLink to="/" end onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? "text-[hsl(var(--tm-gold))]" : "tm-gold-link"}>
                        {t.accueil}
                    </NavLink>
                    <NavLink to="/expertise" onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? "text-[hsl(var(--tm-gold))]" : "tm-gold-link"}>
                        {t.expertise}
                    </NavLink>
                    <NavLink to="/a-propos" onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? "text-[hsl(var(--tm-gold))]" : "tm-gold-link"}>
                        {t.apropos}
                    </NavLink>
                    <a href="mailto:contact@timemetria.com" className="tm-gold-link">{t.contact}</a>
                </nav>
            )}
        </header>
    );
}
